import { Button, Form, Input, InputNumber, Modal } from 'antd';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNotify } from '@/hooks/useNotify';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useMutationReset } from '@/hooks/useMutationReset';
import { useAdjustInventoryMutation } from '@/modules/inventory/inventoryApi';
import type { InventoryItem } from '@/types/inventory';
import { resolveMutationResult } from '@/utils/apiResponse';

interface AdjustStockModalProps {
  open: boolean;
  item?: InventoryItem | null;
  onClose: () => void;
}

interface AdjustStockFormValues {
  quantity: number;
  reason: string;
}

export function AdjustStockModal({ open, item, onClose }: AdjustStockModalProps) {
  const notify = useNotify();
  const [adjustInventory, adjustState] = useAdjustInventoryMutation();
  useGlobalLoader(adjustState.isLoading);
  useMutationReset(adjustState.reset);

  const { control, handleSubmit, reset } = useForm<AdjustStockFormValues>({
    defaultValues: {
      quantity: 0,
      reason: '',
    },
  });

  useEffect(() => {
    if (!open) return;

    reset({
      quantity: item?.quantity ?? 0,
      reason: '',
    });
  }, [item, open, reset]);

  const onSubmit = async (values: AdjustStockFormValues) => {
    if (!item?.id) return;

    const result = await resolveMutationResult(
      adjustInventory({
        inventoryLevelId: item.id,
        quantity: values.quantity,
        reason: values.reason.trim() || undefined,
      }),
    );

    if (result.ok) {
      notify.success(result.message ?? 'Stock updated successfully.');
      onClose();
      return;
    }

    notify.error(result.message);
  };

  return (
    <Modal
      title="Adjust stock"
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
      width={440}
    >
      {item ? (
        <div style={{ marginBottom: 16, fontSize: 13, color: '#666' }}>
          <div>
            <strong>{item.productName}</strong>
            {item.variantLabel ? ` · ${item.variantLabel}` : null}
          </div>
          <div>
            SKU: {item.sku} · Warehouse: {item.warehouseName} ({item.warehouseCode})
          </div>
        </div>
      ) : null}

      <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
        <Controller
          name="quantity"
          control={control}
          rules={{
            required: 'Quantity is required',
            min: { value: 0, message: 'Quantity cannot be negative' },
          }}
          render={({ field, fieldState }) => (
            <Form.Item
              label="Quantity"
              required
              validateStatus={fieldState.error ? 'error' : ''}
              help={fieldState.error?.message}
            >
              <InputNumber {...field} min={0} style={{ width: '100%' }} />
            </Form.Item>
          )}
        />

        <Controller
          name="reason"
          control={control}
          render={({ field }) => (
            <Form.Item label="Reason (optional)">
              <Input {...field} placeholder="e.g. Restock, correction" />
            </Form.Item>
          )}
        />

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Button onClick={onClose} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={adjustState.isLoading}>
            Update stock
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
}
